import React, { useState } from "react";
import { filter, join, map, uniqBy } from "lodash";
import { Color } from "@glossy";
import { getFacetName } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import { getPlatformColor } from "@linkfluence/models/platform";
import ColorSelector from "@radarly/components/panels/card-edition/color-selector";
import { useContextualFocuses } from "@radarly/core/context/use-cases";
import { useCurrentProjectPlatforms } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import {
  DatavizEditableColors,
  isMultipleLineChartDataviz,
} from "@radarly/models/dataviz";
import { updateDatavizOptions } from "@radarly/models/dataviz";
import { Query } from "@radarly/models/queries";

interface SharedPanelsCardEditionSidebarColorProps<
  D extends DatavizEditableColors
> {
  dataviz: D;
  setDataviz: (dataviz: D) => void;
  closePanel: () => void;
  pageColors: string[];
  index?: number;
}

const getUniqueQueryColors = (queries: Query[]) =>
  uniqBy(
    map(queries, (query) => ({
      name: join(map(filter(queries, { color: query.color }), "label"), ", "),
      hex: query.color,
    })),
    "hex"
  );

const getNewColor = (dataviz: DatavizEditableColors, index: number): string => {
  if (isMultipleLineChartDataviz(dataviz)) {
    if (dataviz.filteredValues && typeof index === "number") {
      return dataviz.filteredValues[index]?.color || Color.LightSeaGreen; //apply default lkf color
    }
  } else {
    return dataviz.color;
  }
};

const getColorValue = (
  dataviz: DatavizEditableColors,
  color: string,
  index: number
) => {
  if (isMultipleLineChartDataviz(dataviz)) {
    const newFilteredValues = dataviz.filteredValues
      ? [...dataviz.filteredValues]
      : [];
    const currentValue = newFilteredValues[index]?.value || "";
    newFilteredValues[index] = { value: currentValue, color };
    return newFilteredValues;
  }
  return color;
};

const SharedPanelsCardEditionSidebarColor = <D extends DatavizEditableColors>({
  dataviz,
  setDataviz,
  closePanel,
  pageColors,
  index,
}: SharedPanelsCardEditionSidebarColorProps<D>) => {
  const hasFilteredValues =
    isMultipleLineChartDataviz(dataviz) && typeof index === "number";
  const newColor = getNewColor(dataviz, index);
  const [currentColor, setColor] = useState(newColor);
  const locale = useCurrentUserLocale();
  const contextualFocuses = useContextualFocuses();
  const platforms = useCurrentProjectPlatforms();
  const updateColor = (color: string) => {
    setDataviz(
      updateDatavizOptions(
        dataviz,
        //@ts-ignore
        hasFilteredValues ? "filteredValues" : "color",
        getColorValue(dataviz, color, index)
      )
    );
  };
  const resetAndClose = () => {
    updateColor(currentColor);
    closePanel();
  };
  const saveAndClose = () => {
    setColor(newColor);
    closePanel();
  };

  return (
    <ColorSelector
      currentColor={newColor}
      locale={locale}
      onClickBack={resetAndClose}
      onCancel={resetAndClose}
      onValidate={saveAndClose}
      queries={getUniqueQueryColors(contextualFocuses)}
      currentlyUsedColors={pageColors}
      platforms={map(platforms, (platform) => ({
        name: getFacetName(locale, Facet.Platforms, platform),
        hex: getPlatformColor(platform),
      }))}
      onChange={(color) => updateColor(color)}
    />
  );
};

export default SharedPanelsCardEditionSidebarColor;
